import { useState, useContext, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Button,
  Dialog,
  TextField,
  Typography,
  DialogContent,
  FormHelperText,
  Select,
  FormControl,
  MenuItem,
} from "@mui/material";
//Import from project
import "../../../Style/dialogstyle.scss";
import { useMutation } from "@apollo/client";
import { AuthContext } from "../../../Context/AuthContext";
import { DialogTitleComp } from "../../../Utils/Component";
import {
  CREATE_EDUCATION_LEVEL,
  UPDATE_EDUCATION_LEVEL,
} from "../../../Schema/EducationLevel";
import {
  SelectInstituteOrUniversity,
  SelectMajors,
  SelectMember,
} from "../../Include/DynamicSelect";
import { SelectDatePicker } from "../../Include/SelectDatePicker";

export default function EducationLevelForm({
  open,
  editData,
  setRefetch,
  dialogTitle,
  handleClose,
  hideBackdrop,
}) {
  // Change Language
  const { t, setAlert } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  // ================================= End upload Image ===============================

  const [createEducationLevel] = useMutation(CREATE_EDUCATION_LEVEL, {
    onCompleted: ({ createEducationLevel }) => {
      setLoading(false);
      if (createEducationLevel?.isSuccess === true) {
        setAlert(true, "success", createEducationLevel?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", createEducationLevel?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  const [updateEducationLevel] = useMutation(UPDATE_EDUCATION_LEVEL, {
    onCompleted: ({ updateEducationLevel }) => {
      setLoading(false);
      if (updateEducationLevel?.isSuccess === true) {
        setAlert(true, "success", updateEducationLevel?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", updateEducationLevel?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });
  const queryParams = new URLSearchParams(window.location.search);
  const id = queryParams.get("id");
  //fromik
  const CheckValidation = Yup.object().shape({
    level: Yup.string().required(t("required")),
    endDate: Yup.string().required(t("required")),
    startDate: Yup.string().required(t("required")),
    majorsId: Yup.object().shape({
      id: Yup.string().required(t("required")),
      title: Yup.string(),
    }),
    instituteOrUniversityId: Yup.object().shape({
      id: Yup.string().required(t("required")),
      title: Yup.string(),
    }),
  });

  const formik = useFormik({
    initialValues: {
      level: "",
      endDate: "",
      startDate: "",
      majorsId: { id: "", title: "" },
      instituteOrUniversityId: { id: "", title: "" },
    },

    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      setLoading(true);
      if (dialogTitle === "Create") {
        createEducationLevel({
          variables: {
            input: {
              ...values,
              memberId: id,
              majorsId: values?.majorsId?.id,
              instituteOrUniversityId: values?.instituteOrUniversityId?.id,
            },
          },
        });
      } else {
        updateEducationLevel({
          variables: {
            id: editData?._id,
            input: {
              ...values,
              memberId: id,
              majorsId: values?.majorsId?.id,
              instituteOrUniversityId: values?.instituteOrUniversityId?.id,
            },
          },
        });
      }
    },
  });

  const {
    errors,
    values,
    touched,
    handleSubmit,
    setFieldValue,
    getFieldProps,
    resetForm,
  } = formik;

  useEffect(() => {
    if (editData) {
      setFieldValue("level", editData?.level);
      setFieldValue("endDate", editData?.endDate);
      setFieldValue("startDate", editData?.startDate);
      setFieldValue("majorsId", {
        id: editData?.majorsId?._id,
        title: editData?.majorsId?.majorsNameKh,
      });
      setFieldValue("instituteOrUniversityId", {
        id: editData?.instituteOrUniversityId?._id,
        title: editData?.instituteOrUniversityId?.nameKh,
      });
    }
  }, [editData, open]);
  const levelData = [
    {
      value: "Postdoctoral Studies",
      label: t("thead_postdoctoral_studies"),
    },
    {
      value: "Doctor of Philosophy",
      label: t("thead_doctor_of_philosophy"),
    },
    {
      value: "Master's Degree",
      label: t("thead_masters_degree"),
    },
    {
      value: "Bachelor's Degree",
      label: t("thead_bachelor_degree"),
    },
    {
      value: "Associate Degree",
      label: t("thead_associate_degree"),
    },
    {
      value: "Professional Certifications",
      label: t("thead_professional_certifications"),
    },
    {
      value: "High School Diploma",
      label: t("thead_high_school_diploma"),
    },
    {
      value: "Secondary School",
      label: t("thead_secondary_school"),
    },
    {
      value: "Elementary School",
      label: t("thead_elementary_school"),
    },
  ];
  return (
    <Dialog
      fullWidth
      open={open}
      maxWidth="xs"
      className="dialog-container"
      hideBackdrop={hideBackdrop}
    >
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_education_level")
            : t("modal_title_update_education_level")
        }
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleClose}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12}>
                <SelectInstituteOrUniversity
                  buttonAdd={true}
                  onChange={(val) =>
                    setFieldValue("instituteOrUniversityId", val)
                  }
                  value={values?.instituteOrUniversityId}
                  error={Boolean(
                    touched.instituteOrUniversityId?.id &&
                      errors.instituteOrUniversityId?.id
                  )}
                  helperText={
                    touched.instituteOrUniversityId?.id &&
                    errors.instituteOrUniversityId?.id
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <SelectMajors
                  buttonAdd={true}
                  onChange={(val) => setFieldValue("majorsId", val)}
                  value={values?.majorsId}
                  error={Boolean(touched.majorsId?.id && errors.majorsId?.id)}
                  helperText={touched.majorsId?.id && errors.majorsId?.id}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_level")}
                </Typography>
                <FormControl className={"text-field"} fullWidth size="small">
                  <Select
                    {...getFieldProps("level")}
                    error={Boolean(touched.level && errors.level)}
                  >
                    {levelData?.map((e) => (
                      <MenuItem value={e?.value}>{e?.label}</MenuItem>
                    ))}
                  </Select>
                  {!!errors?.level && touched?.level && (
                    <FormHelperText error id="outlined-adornment-password">
                      {errors?.level}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_start_date")}
                </Typography>
                <SelectDatePicker
                  value={values?.startDate}
                  onChange={(val) => setFieldValue("startDate", val)}
                />
                {!!errors?.startDate && touched?.startDate && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors?.startDate}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_end_date")}
                </Typography>
                <SelectDatePicker
                  value={values?.endDate}
                  onChange={(val) => setFieldValue("endDate", val)}
                />
                {!!errors?.endDate && touched?.endDate && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors?.endDate}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12}>
                {loading ? (
                  <Button className={t("class-btn-create")} fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    className={t("class-btn-create")}
                    fullWidth
                    onClick={handleSubmit}
                  >
                    {dialogTitle === "Create" ? t("create") : t("update")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
